import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Trade {
  ticker: string;
  exchange: string;
  specific_exchange: string;
  action: string;
  quantity: number;
  entry_price: number | null;
  exit_price: number | null;
  pnl: number | null;
  pnl_pct: number | null;
  entry_date: string | null;
  exit_date: string | null;
  hold_duration: string | null;
  buy_score: number | null;
  buy_score_min: number | null;
  buy_score_max: number | null;
  sell_score: number | null;
  is_manual: boolean;
  order_id?: string | null;
  broker_order_id?: string | null;
  status?: string;
  reason?: string;
  submitted_at?: string | null;
}

interface Summary {
  total_trades: number;
  win_rate: number;
  total_pnl: number;
  avg_return: number;
}

const API = '/api';

function api(path: string, token: string) {
  return fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

function toAEST(isoStr: string | null): string {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

function toAESTDate(isoStr: string | null): string {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleDateString('en-AU', {
      timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return isoStr; }
}

function toAESTTime(isoStr: string | null): string {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleTimeString('en-AU', {
      timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

/**
 * Convert a local date string (YYYY-MM-DD from the date input, treated as AEST)
 * into a UTC ISO string for comparison with server timestamps.
 * AEST = UTC+10, so AEST midnight = previous day 14:00 UTC.
 */
function toUTCFromAEST(dateStr: string, endOfDay = false): string {
  const suffix = endOfDay ? 'T23:59:59+10:00' : 'T00:00:00+10:00';
  return new Date(dateStr + suffix).toISOString();
}

function PnLCell({ pnl, pnl_pct }: { pnl: number | null; pnl_pct: number | null }) {
  if (pnl === null) return <span className="text-text-muted">—</span>;
  const pos = pnl >= 0;
  return (
    <span className={pos ? 'text-status-green' : 'text-status-red'}>
      {pos ? '+' : ''}${Math.abs(pnl).toFixed(4)}
      {pnl_pct !== null && (
        <span className="text-xs ml-1">({pos ? '+' : ''}{pnl_pct.toFixed(2)}%)</span>
      )}
    </span>
  );
}

function TradeHistory() {
  const token = sessionStorage.getItem('token') || '';
  const navigate = useNavigate();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  // Root cause of the flicker/scroll-reset: the old code reused `loading` for every
  // 30s background poll, which unmounted the whole table (loading gates its render).
  // `refreshing` covers background polls without touching the table's mount state.
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Fix 3F: named filter states matching clearFilters expectations
  const [filterExchange, setFilterExchange] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterTicker, setFilterTicker] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const loadTrades = async (overrides?: {
    exchange?: string;
    action?: string;
    from?: string;
    to?: string;
  }) => {
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const exc = overrides?.exchange !== undefined ? overrides.exchange : filterExchange;
      const act = overrides?.action !== undefined ? overrides.action : filterAction;
      const from = overrides?.from !== undefined ? overrides.from : filterFrom;
      const to = overrides?.to !== undefined ? overrides.to : filterTo;

      const res = await api(`/trades?limit=1000&offset=0`, token);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();

      const fromUtc = from ? toUTCFromAEST(from, false) : '';
      const toUtc = to ? toUTCFromAEST(to, true) : '';

      const mapped: Trade[] = (data.trades || []).map((t: any) => {
        const isBuy = (t.action || '').toUpperCase() === 'BUY';
        const isSell = (t.action || '').toUpperCase() === 'SELL';
        const avgCost = t.avg_cost_at_trade != null ? Number(t.avg_cost_at_trade) : null;
        const qty = Number(t.quantity || 0);
        const price = t.price != null ? Number(t.price) : null;
        const pnl = t.pnl != null ? Number(t.pnl) : null;
        const pnl_pct = pnl != null && avgCost && avgCost > 0 && qty > 0
          ? (pnl / (avgCost * qty)) * 100 : null;
        const dh = t.duration_hours != null ? Number(t.duration_hours) : null;
        const hold_duration = dh != null
          ? (dh >= 24 ? `${(dh / 24).toFixed(1)} days` : `${dh.toFixed(1)} hrs`)
          : null;
        return {
          ticker: t.ticker,
          exchange: t.market || t.exchange,
          specific_exchange: t.exchange,
          action: (t.action || '').toUpperCase(),
          quantity: qty,
          entry_price: isBuy ? price : avgCost,
          exit_price: isSell ? price : null,
          pnl,
          pnl_pct,
          entry_date: isBuy ? (t.executed_at || null) : null,
          exit_date: isSell ? (t.executed_at || null) : null,
          hold_duration,
          buy_score: t.buy_signal_score != null ? Number(t.buy_signal_score) : null,
          buy_score_min: t.buy_score_min != null ? Number(t.buy_score_min) : null,
          buy_score_max: t.buy_score_max != null ? Number(t.buy_score_max) : null,
          sell_score: isSell && t.signal_score != null ? Number(t.signal_score) : null,
          is_manual: t.is_manual ?? false,
          status: t.status,
          reason: t.notes ?? undefined,
          submitted_at: (t.status === 'failed' || (t.action || '').toUpperCase() === 'CANCELLED') ? (t.executed_at || null) : null,
        };
      });

      // Client-side filtering
      const filtered = mapped.filter(t => {
        if (exc && exc !== 'all' && t.exchange !== exc) return false;
        if (act && act !== 'all' && t.action !== act.toUpperCase()) return false;
        const dateStr = t.exit_date || t.entry_date || t.submitted_at || null;
        if (fromUtc && dateStr && dateStr < fromUtc) return false;
        if (toUtc && dateStr && dateStr > toUtc) return false;
        return true;
      });

      // Compute summary client-side from filtered trades
      const pnls = filtered.map(t => t.pnl).filter((p): p is number => p != null);
      const pnl_pcts = filtered.map(t => t.pnl_pct).filter((p): p is number => p != null);
      const wins = pnls.filter(p => p > 0);
      const total_pnl = pnls.reduce((a, b) => a + b, 0);
      const win_rate = pnls.length > 0 ? (wins.length / pnls.length) * 100 : 0;
      const avg_return = pnl_pcts.length > 0 ? pnl_pcts.reduce((a, b) => a + b, 0) / pnl_pcts.length : 0;
      setSummary({
        total_trades: filtered.length,
        win_rate: Math.round(win_rate * 10) / 10,
        total_pnl: Math.round(total_pnl * 100) / 100,
        avg_return: Math.round(avg_return * 100) / 100,
      });
      setTrades(filtered);
    } catch (e: any) {
      setError(e.message || 'Failed to load trade history');
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnce.current = true;
    }
  };

  // Latest-ref pattern: the interval below is only ever created once (empty deps,
  // so it doesn't reset every render), so its callback must not close over the
  // filter state from that first render. Without this, every 30s background poll
  // silently re-fetched using the filters that were active on first mount, not
  // whatever the user had applied since - the "results changing on their own"
  // symptom underneath the visible flicker.
  const loadTradesRef = useRef(loadTrades);
  loadTradesRef.current = loadTrades;

  useEffect(() => {
    loadTradesRef.current();
    const interval = setInterval(() => loadTradesRef.current(), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    loadTrades();
  };

  // Fix 3F: reset ALL filters to defaults, then reload immediately with cleared values
  const clearFilters = () => {
    setFilterExchange('all');
    setFilterAction('all');
    setFilterTicker('');
    setFilterFrom('');
    setFilterTo('');
    loadTrades({ exchange: 'all', action: 'all', from: '', to: '' });
  };

  // Fix 3D: CSV export from currently loaded trades (optionally filtered by ticker in-browser)
  const exportCSV = () => {
    const headers = [
      'Date', 'Ticker', 'Exchange', 'Action', 'Quantity',
      'Entry Price', 'Exit Price', 'Total Value', 'Buy Score', 'Buy Score Min', 'Buy Score Max', 'Sell Score',
      'Manual', 'Duration', 'P&L', 'Order ID', 'Broker ID', 'Status', 'Reason', 'Submitted',
    ];
    const displayedTrades = filteredTrades;
    const rows = displayedTrades.map(t => [
      toAEST(t.exit_date || t.entry_date),
      t.ticker,
      t.specific_exchange || t.exchange,
      t.action,
      t.quantity.toFixed(2),
      t.entry_price != null ? t.entry_price.toFixed(4) : '',
      t.exit_price != null ? t.exit_price.toFixed(4) : '',
      t.entry_price != null ? (t.quantity * t.entry_price).toFixed(4)
        : t.exit_price != null ? (t.quantity * t.exit_price).toFixed(4) : '',
      t.buy_score != null ? t.buy_score.toFixed(2) : '',
      t.buy_score_min != null ? t.buy_score_min.toFixed(2) : '',
      t.buy_score_max != null ? t.buy_score_max.toFixed(2) : '',
      t.sell_score != null ? t.sell_score.toFixed(2) : '',
      t.is_manual ? 'Yes' : 'No',
      t.hold_duration || '',
      t.pnl != null ? t.pnl.toFixed(2) : '',
      t.order_id || '',
      t.broker_order_id || '',
      t.status || 'filled',
      t.reason || '',
      t.submitted_at ? toAEST(t.submitted_at) : '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trade_history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Client-side ticker filter (applied on top of server-side results)
  const filteredTrades = filterTicker.trim()
    ? trades.filter(t => t.ticker.toLowerCase().includes(filterTicker.trim().toLowerCase()))
    : trades;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-text-bright">Trade History</h1>
          {refreshing && <span className="text-xs text-text-muted animate-pulse">Refreshing…</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* Fix 3D: Export CSV button */}
          <button
            onClick={exportCSV}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-3 py-1.5 rounded-btn flex items-center gap-1"
          >
            Export CSV
          </button>
          <button
            onClick={() => loadTrades()}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-3 py-1 rounded-btn"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-card p-4 border border-border-base">
            <p className="text-text-secondary text-sm">Total Trades</p>
            <p className="text-2xl font-bold mt-1 text-text-bright">{summary.total_trades}</p>
          </div>
          <div className="bg-surface rounded-card p-4 border border-border-base">
            <p className="text-text-secondary text-sm">Win Rate</p>
            <p className={`text-2xl font-bold mt-1 ${summary.win_rate >= 50 ? 'text-status-green' : 'text-status-red'}`}>
              {summary.win_rate.toFixed(1)}%
            </p>
          </div>
          <div className="bg-surface rounded-card p-4 border border-border-base">
            <p className="text-text-secondary text-sm">Total P&L</p>
            <p className={`text-2xl font-bold mt-1 ${summary.total_pnl >= 0 ? 'text-status-green' : 'text-status-red'}`}>
              {summary.total_pnl >= 0 ? '+' : ''}${summary.total_pnl.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-surface rounded-card p-4 border border-border-base">
            <p className="text-text-secondary text-sm">Avg Return / Trade</p>
            <p className={`text-2xl font-bold mt-1 ${summary.avg_return >= 0 ? 'text-status-green' : 'text-status-red'}`}>
              {summary.avg_return >= 0 ? '+' : ''}{summary.avg_return.toFixed(2)}%
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleApplyFilters} className="bg-surface border border-border-base rounded-card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Exchange</label>
            <select
              value={filterExchange}
              onChange={e => setFilterExchange(e.target.value)}
              className="bg-surface-2 border border-border-light rounded-btn-sm px-3 py-1.5 text-sm text-text-primary"
            >
              <option value="all">All</option>
              <option value="ASX">ASX</option>
              <option value="US">NYSE/NASDAQ</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Action</label>
            <select
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
              className="bg-surface-2 border border-border-light rounded-btn-sm px-3 py-1.5 text-sm text-text-primary"
            >
              <option value="all">All</option>
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Ticker</label>
            <input
              type="text"
              value={filterTicker}
              onChange={e => setFilterTicker(e.target.value)}
              placeholder="e.g. BHP"
              className="bg-surface-2 border border-border-light rounded-btn-sm px-3 py-1.5 text-sm w-24 text-text-primary"
            />
          </div>
          <div>
            {/* Fix 3E: date inputs treated as AEST, converted to UTC in loadTrades */}
            <label className="block text-xs text-text-secondary mb-1">From Date (AEST)</label>
            <input
              type="date"
              value={filterFrom}
              onChange={e => setFilterFrom(e.target.value)}
              className="bg-surface-2 border border-border-light rounded-btn-sm px-3 py-1.5 text-sm text-text-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">To Date (AEST)</label>
            <input
              type="date"
              value={filterTo}
              onChange={e => setFilterTo(e.target.value)}
              className="bg-surface-2 border border-border-light rounded-btn-sm px-3 py-1.5 text-sm text-text-primary"
            />
          </div>
          <button
            type="submit"
            className="bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright text-sm px-4 py-1.5 rounded-btn"
          >
            Apply
          </button>
          {/* Fix 3F: Clear resets all filters to defaults */}
          <button
            type="button"
            onClick={clearFilters}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright text-sm px-4 py-1.5 rounded-btn"
          >
            Clear
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bg-surface rounded-card border border-border-base overflow-hidden">
        {loading && (
          <div className="p-6 text-text-secondary text-sm text-center">Loading trade history...</div>
        )}
        {error && (
          <div className="p-6 text-status-red text-sm text-center">{error}</div>
        )}
        {!loading && !error && filteredTrades.length === 0 && (
          <div className="p-6 text-text-secondary text-sm text-center">
            No completed trades yet — trades appear here once positions are closed.
          </div>
        )}
        {!loading && !error && filteredTrades.length > 0 && (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg-deep z-10">
                <tr className="text-text-muted border-b border-border-base text-xs uppercase tracking-wide">
                  <th className="text-left p-3">Date (AEST)</th>
                  <th className="text-left p-3">Ticker</th>
                  <th className="text-left p-3">Exchange</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Entry</th>
                  <th className="text-right p-3">Exit</th>
                  <th className="text-right p-3">P&L</th>
                  <th className="text-right p-3">
                    <span title="Time held between buy and sell" className="cursor-help border-b border-dotted border-border-light">Duration</span>
                  </th>
                  <th className="text-right p-3">Buy Score</th>
                  <th className="text-right p-3">Sell Score</th>
                  <th className="text-center p-3">Manual</th>
                  <th className="text-left p-3">Order ID</th>
                  <th className="text-left p-3">Broker ID</th>
                  <th className="text-left p-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrades.map((t, i) => {
                  const dateStr = t.exit_date || t.entry_date || t.submitted_at || null;
                  // Keyed by order_id (stable across polls) with a composite fallback
                  // for rows that predate order_id tracking - not the array index,
                  // which caused every poll to remount rows whose position shifted.
                  const rowKey = t.order_id || `${t.ticker}-${t.action}-${dateStr}-${i}`;
                  return (
                  <tr key={rowKey} className={`border-b border-border-base hover:bg-surface-hover ${t.status === 'failed' ? 'opacity-60' : ''}`}>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {t.status === 'failed' ? (
                        <div>
                          <span className="text-status-red font-medium">FAILED</span>
                          {t.submitted_at && <div className="text-text-muted text-xs">{toAESTDate(t.submitted_at)}</div>}
                        </div>
                      ) : t.action === 'CANCELLED' ? (
                        <div>
                          <div className="text-text-secondary">{t.submitted_at ? toAESTDate(t.submitted_at) : '—'}</div>
                          <div className="text-text-muted">{t.submitted_at ? toAESTTime(t.submitted_at) : ''}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-text-primary">{toAESTDate(dateStr)}</div>
                          <div className="text-text-muted">{toAESTTime(dateStr)}</div>
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-medium">
                      <button
                        onClick={() => navigate(`/research?ticker=${t.ticker}&exchange=${t.specific_exchange || t.exchange}`)}
                        className="text-status-green hover:text-status-green-muted underline underline-offset-2 font-medium"
                      >
                        {t.ticker}
                      </button>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded-badge ${
                        t.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'
                      }`}>
                        {t.specific_exchange || t.exchange}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`font-semibold ${t.action === 'BUY' ? 'text-status-green' : 'text-status-red'}`}>
                        {t.action}
                      </span>
                    </td>
                    <td className="p-3 text-right">{t.quantity.toFixed(2)}</td>
                    <td className="p-3 text-right text-xs">
                      {t.entry_price != null ? `$${t.entry_price.toFixed(4)}` : '—'}
                    </td>
                    <td className="p-3 text-right text-xs">
                      {t.exit_price != null ? `$${t.exit_price.toFixed(4)}` : '—'}
                    </td>
                    <td className="p-3 text-right">
                      <PnLCell pnl={t.pnl} pnl_pct={t.pnl_pct} />
                    </td>
                    <td className="p-3 text-right text-text-secondary">
                      {t.hold_duration || '—'}
                    </td>
                    <td className="p-3 text-right">
                      {(() => {
                        const isSell = t.action === 'SELL';
                        if (isSell && t.buy_score_min != null && t.buy_score_max != null) {
                          const rangeStr = t.buy_score_min === t.buy_score_max
                            ? t.buy_score_min.toFixed(2)
                            : `${t.buy_score_min.toFixed(2)}–${t.buy_score_max.toFixed(2)}`;
                          const high = t.buy_score_min >= 6.5;
                          return <span className={high ? 'text-status-green' : 'text-text-primary'}>{rangeStr}</span>;
                        }
                        if (t.buy_score != null) {
                          return (
                            <span className={t.buy_score >= 6.5 ? 'text-status-green' : 'text-text-primary'}>
                              {t.buy_score.toFixed(2)}
                            </span>
                          );
                        }
                        return <span className="text-text-muted">—</span>;
                      })()}
                    </td>
                    <td className="p-3 text-right">
                      {t.sell_score != null ? (
                        <span className={t.sell_score <= 3.5 ? 'text-status-red' : 'text-text-primary'}>
                          {t.sell_score.toFixed(2)}
                        </span>
                      ) : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="p-3 text-center">
                      {t.is_manual ? <span className="text-status-amber">✓</span> : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="p-3 text-xs text-text-muted font-mono">
                      {t.order_id ? (
                        <span title={t.order_id} className="cursor-help">
                          {t.order_id.slice(0, 8)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-xs text-text-muted font-mono max-w-[100px] truncate" title={t.broker_order_id || ''}>
                      {t.broker_order_id || '—'}
                    </td>
                    <td className="p-3 text-xs max-w-[200px] truncate" title={t.reason || ''}>
                      {t.status === 'failed' ? (
                        <span className="text-status-red">{t.reason || 'Unknown error'}</span>
                      ) : t.action === 'CANCELLED' ? (
                        <span className="text-text-secondary">{t.reason || 'Cancelled'}</span>
                      ) : (
                        <span className="text-text-muted">{t.submitted_at ? toAESTTime(t.submitted_at) : ''}</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TradeHistory;
